import React from 'react';
import { motion } from 'motion/react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LazyImage } from '@/components/ui/LazyImage';

export function FrameworkPage() {
  const { t } = useTranslation();

  const lenses = [
    {
      id: '01',
      title: t('framework.lens1.title', 'The Enneagram'),
      description: 'Serving as the core lens and starting point for insights, The Enneagram reveals the fundamental drivers of behavior, potential blind spots, and how individuals interact with others under pressure. Instead of categorizing individuals into fixed "types," we measure how frequently each of the nine Enneagram styles manifests.',
      image: '/enneagram-teal-corrected.webp',
      imageAlt: 'The Enneagram nine-pointed diagram illustrating core personality motivations and defence mechanisms',
      url: '/lens/enneagram'
    },
    {
      id: '02',
      title: t('framework.lens2.title', 'Personal Mastery & Maturity'),
      description: 'Measures psychological resilience, self-efficacy, and leadership maturity (vertical development). This lens reveals thought patterns and emotional habits, helping leaders address limiting beliefs and foster significant mindset shifts to adapt to rapid organizational change.',
      image: '/personal-mastery-DETAILED.webp',
      imageAlt: 'Personal Mastery and Maturity lens diagram showing self-efficacy, resilience, and vertical leadership development dimensions',
      url: '/lens/personal-mastery'
    },
    {
      id: '03',
      title: t('framework.lens3.title', 'Emotional Resilience'),
      description: 'Assesses the capacity to adapt, recover, and maintain clarity and effectiveness when under strain. This lens evaluates emotional agility, stress tolerance, and the ability to navigate complex interpersonal dynamics without losing focus.',
      image: '/resilience-DETAILED.webp',
      imageAlt: 'Emotional Resilience lens diagram showing capacity to adapt, recover, and maintain effectiveness under strain',
      url: '/lens/emotional-resilience'
    },
    {
      id: '04',
      title: t('framework.lens4.title', 'Social Drives'),
      description: 'Analyzes how individuals navigate status, connection, and belonging within teams. By uncovering fundamental motivations and unmet needs, this lens provides actionable insights into resolving systemic team friction and improving collaboration.',
      image: '/social-drives-DETAILED.webp',
      imageAlt: 'Social Drives lens diagram showing how individuals navigate status, connection, and belonging within teams',
      url: '/lens/social-drives'
    },
    {
      id: '05',
      title: t('framework.lens5.title', 'Energy Centres'),
      description: t('framework.lens5.pageDesc', 'Explores how individuals process information and make decisions using three core centres: Instinctual, Emotional and Intellectual. Understanding these centres helps leaders communicate more effectively and align executive teams.'),
      image: '/energy-centres-DETAILS.webp',
      imageAlt: 'Energy Centers lens diagram showing Instinctual, Emotional, and Intellectual processing centres',
      url: '/lens/energy-centres'
    }
  ];

  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[{ label: t('nav.framework', 'The Framework') }]} />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="mb-6 flex items-center gap-3">
            <span className="h-[1px] w-8 bg-rule-3"></span>
            <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-rule-3">The Methodology</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-serif leading-tight mb-8 text-ink font-normal">
            Beyond Behavioral Typology. <br />
            <span className="italic text-rule-3">A Multidimensional Approach.</span>
          </h1>
          
          <p className="text-xl text-ink/70/80 leading-relaxed max-w-3xl font-light mb-12">
            Human behavior is too complex for a single lens. By integrating five distinct layers of psychological insight, the FIVE LENS framework provides unprecedented, data-driven depth for coaching, culture change, and leadership development.
          </p>
          <div className="flex justify-center w-full mb-16">
            <LazyImage 
              src="/5-lens-infographic-updated.webp"
              alt="FIVE LENS Methodology Composite"
              className="w-full max-w-4xl object-contain mix-blend-multiply"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-12 mt-16">
            <div className="bg-white p-10 border border-ground-deep">
              <h3 className="text-2xl font-serif text-ink mb-4">The Problem with Single-Typing</h3>
              <p className="text-ink/70/80 leading-relaxed font-light">
                Traditional personality assessments often reduce complex human behavior to fixed categories or four-quadrant grids. While useful for basic self-awareness, these single-lens models fail to account for contextual shifts, developmental maturity, and subconscious defense mechanisms.
              </p>
            </div>
            <div className="bg-ink text-white p-10 border border-ground-deep">
              <h3 className="text-2xl font-serif text-rule-3 mb-4">The FIVE LENS Solution</h3>
              <p className="text-white leading-relaxed font-light">
                We measure how behaviors shift dynamically under stress or growth. By overlapping the Enneagram with four proven psychological models, practitioners gain a high-resolution map of an individual's or team's operational reality, allowing for precise, root-cause interventions.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="space-y-32">
          {lenses.map((lens, index) => (
            <motion.div
              key={lens.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className={`flex flex-col ${index % 2 !== 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-24 items-center`}
            >
              <div className="w-full lg:w-1/2 relative group">
                <LazyImage 
                  src={lens.image} 
                  alt={lens.imageAlt || lens.title} 
                  className="w-full max-w-md mx-auto object-contain mix-blend-multiply relative z-10 p-8 bg-white border border-ground-deep"
                />
              </div>
              
              <div className="w-full lg:w-1/2">
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-serif text-lg font-semibold text-rule-3">{lens.id}</span>
                  <h2 className="text-3xl font-serif text-ink">{lens.title}</h2>
                </div>
                
                <p className="text-lg text-ink/70/80 leading-relaxed font-light mb-8">
                  {lens.description}
                </p>
                
                <Link 
                  to={lens.url} 
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink hover:text-rule-3 border-b border-ink hover:border-rule-3 pb-1 transition-colors"
                >
                  Explore {lens.title} in depth <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 bg-white p-12 border border-ground-deep text-center"
        >
          <h2 className="text-3xl font-serif text-ink mb-6">Ready to apply the framework?</h2>
          <p className="text-lg text-ink/70/80 mb-10 max-w-2xl mx-auto">
            Whether you are an independent coach looking to become certified, or an enterprise leader seeking to resolve team friction, the FIVE LENS methodology provides the depth you need.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/practitioner-certification" 
              className="bg-ink text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ink/70 transition-colors"
            >
              Practitioner Certification
            </Link>
            <Link 
              to="/team-effectiveness" 
              className="border border-ink text-ink px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ground transition-colors"
            >
              Enterprise Solutions
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
